import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { usePathname } from "@/hooks/use-pathname-compat";
import { useMediaQuery } from "@/hooks/use-media-query";
import { track } from "@/lib/analytics";
import {
  advanceGuide,
  dismissGuide,
  getGuideSnapshot,
  retreatGuide,
  subscribeGuide,
} from "@/lib/guide/store";
import type {
  GuideAdvance,
  GuideAdvanceButtonPos,
  GuideStep,
} from "@/lib/guide/types";
import {
  adaptivePadding,
  readRadius,
  spotlightElementFor,
} from "@/lib/guide/geometry";
import {
  queryGuideTarget,
  waitForGuideTarget,
} from "@/lib/guide/resolveTarget";
import {
  unionRect,
  useOpenFloatingRects,
  useTrackedRect,
} from "@/lib/guide/useTrackedRect";
import type { TrackedRect } from "@/lib/guide/useTrackedRect";
import { GuideBackdrop, type GuideHole } from "./GuideBackdrop";
import { GuideCallout } from "./GuideCallout";
import { GuideAdvanceButton } from "./GuideAdvanceButton";
import { GuideCaption } from "./GuideCaption";
import { GuideSpotlight } from "./GuideSpotlight";

/**
 * Singleton guidance overlay, mounted once in PageContent. Renders NOTHING
 * while no guide is active — idle cost is one useSyncExternalStore
 * subscription. When a guide runs, it portals a spotlight + callout to
 * document.body (escaping transformed/virtualized stacking contexts, same
 * reason the shadcn tooltip portals). It is NON-BLOCKING: no scrim, and the
 * callout passes clicks through, so the user drives the real UI and the guide
 * follows.
 */
export function GuideOverlay() {
  const state = useSyncExternalStore(subscribeGuide, getGuideSnapshot);

  // Esc dismisses — listener exists only while a guide is active.
  useEffect(() => {
    if (!state) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") dismissGuide();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [state]);

  if (!state) return null;
  const step = state.guide.steps[state.stepIndex];
  return createPortal(
    <ActiveGuideStep
      // Keyed remount per step: resolution/listeners reset cleanly.
      key={`${state.guide.guideId}:${state.stepIndex}`}
      step={step}
      stepIndex={state.stepIndex}
      stepCount={state.guide.steps.length}
      guideId={state.guide.guideId}
      justRetreated={state.justRetreated}
    />,
    document.body,
  );
}

type ResolutionStatus = "resolving" | "found" | "missing";

const MAX_REATTACH_ATTEMPTS = 3;

// Ceiling for an action-advance wait (appear / navigate). The real action is
// the user's, so the budget is generous — but bounded, so a DOOMED step (an
// `appearTarget` that never mounts, an `advanceTo` that can never match) ends
// loudly instead of lingering forever with a live-looking but dead overlay.
const ACTION_TIMEOUT_MS = 120_000;

// Optional steps skip fast: their target resolves INSTANTLY when present (the
// resolver queries synchronously first), so the only thing the wait buys is a
// short grace for a late mount before skipping — no need for the full 8s drift
// timeout, which would otherwise leave a clean order idling before its success.
const OPTIONAL_TARGET_TIMEOUT_MS = 800;

// The guide's own layers top out at z-index 8010 (see GuideCaption). A floating
// surface the interaction opens — a portaled picker dropdown, calendar, or
// dialog — stacks above that (the app's dropdowns sit at 9999). Anything at or
// above this threshold is treated as part of the current action, never a
// click-away decline.
//
// Deliberately BELOW the app dropdowns so those stay "above the guide" and
// clickable. This is only the INTERACTION threshold — the guide's VISUAL layers
// (spotlight ring z-10050, caption/callout z-10060) sit ABOVE the dropdowns so
// the contour shows over an open picker; being pointer-events-none they don't
// need to match this threshold. Do not "align" the two: raising this above 9999
// would block the picker the guide is pointing at.
const GUIDE_TOP_Z = 8010;

/**
 * Is `node` inside a floating layer stacked above the guide? Walks ancestors
 * reading the effective z-index of positioned elements; a portaled popover
 * (rendered at the document root, so not a DOM child of the target) is caught
 * here where `target.contains(node)` cannot see it.
 */
function isAboveGuide(node: Element | null): boolean {
  // A floating surface the interaction opened (a shadcn/Radix popover,
  // dropdown, or dialog) is part of the current action, not a click-away — even
  // when its own z-index sits BELOW the guide (the date-range popover renders
  // its content at z-50). Match the portal wrapper / content by data-attr so we
  // catch it regardless of z-index, then fall back to the z-index walk.
  if (
    node?.closest(
      "[data-radix-popper-content-wrapper],[data-radix-popover-content],[data-radix-menu-content],[role='dialog']",
    )
  ) {
    return true;
  }
  for (let e = node; e; e = e.parentElement) {
    const style = getComputedStyle(e);
    if (style.position === "static") continue;
    const z = Number.parseInt(style.zIndex, 10);
    if (Number.isFinite(z) && z >= GUIDE_TOP_Z) return true;
  }
  return false;
}

function resolveAdvanceMode(step: GuideStep): GuideAdvance {
  if (step.advanceOn) return step.advanceOn;
  return step.target ? "click-target" : "next";
}

// Pick a fixed slot for the Previous/Next row that does NOT sit on the bright
// region. The control row defaults to bottom-center; a hand-authored walkthrough
// overrides it per step (`advanceButtonPos`), but AI guides never set one — so a
// target low on the screen (an export button, a batch bar) would get the row
// dropped on top of it. When the spotlit region reaches into the bottom-center
// pill zone and the top is clear, flip the row to top-center. `rect` is the
// caption anchor (target + anything it opened), so it dodges an open dropdown
// too. Viewport-relative math (the row is `position: fixed`).
const _ADV_PILL_HALF_WIDTH = 170; // ~300px row + breathing room
function chooseAdvanceButtonPos(rect: TrackedRect): GuideAdvanceButtonPos {
  if (typeof window === "undefined") return "bottom-center";
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const overlapsBottomPill =
    rect.top + rect.height > vh - 96 && // reaches the bottom pill band
    rect.left < vw / 2 + _ADV_PILL_HALF_WIDTH &&
    rect.left + rect.width > vw / 2 - _ADV_PILL_HALF_WIDTH;
  const topZoneClear = rect.top > 140; // top-center pill band is clear
  return overlapsBottomPill && topZoneClear ? "top-center" : "bottom-center";
}

// Corner radius for a floating surface's own cutout (popovers/dropdowns are
// ~8px rounded); the target's container uses its measured radius instead.
const POPOVER_RADIUS = 8;

/** Pad + round a tracked rect into a backdrop hole (viewport coords). */
function paddedHole(rect: TrackedRect, radius: number): GuideHole {
  const pad = adaptivePadding(rect, true);
  return {
    x: rect.left - pad,
    y: rect.top - pad,
    w: rect.width + pad * 2,
    h: rect.height + pad * 2,
    r: radius + pad,
  };
}

function ActiveGuideStep({
  step,
  stepIndex,
  stepCount,
  guideId,
  justRetreated,
}: Readonly<{
  step: GuideStep;
  stepIndex: number;
  stepCount: number;
  guideId: string;
  justRetreated: boolean;
}>) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const pathname = usePathname();
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const advanceOn = resolveAdvanceMode(step);

  const onRoute =
    !step.route ||
    pathname === step.route ||
    (!step.routeExact && (pathname?.startsWith(`${step.route}/`) ?? false));

  const [status, setStatus] = useState<ResolutionStatus>("resolving");
  const [el, setEl] = useState<HTMLElement | null>(null);
  const [attempt, setAttempt] = useState(0);
  // Whether this step's target ever resolved. Distinguishes genuine drift
  // (never found → developer error → toast) from the user simply leaving the
  // context (found, then the popover closed → end silently). No UX difference
  // beyond the toast; it keeps the drift analytics honest.
  const everResolvedRef = useRef(false);

  // End a DOOMED step loudly: a bounded action wait (appear / navigate) that
  // never fires ends the guide with a toast + drift analytics, the same
  // terminal a never-resolving target uses — never a silent, lingering overlay.
  const failStep = useCallback(() => {
    toast.error(t("guide.failed"));
    track("guide_target_missing", {
      guide_id: guideId,
      step_index: stepIndex,
      target: step.target ?? step.appearTarget ?? step.advanceTo ?? "",
    });
    dismissGuide();
  }, [t, guideId, stepIndex, step.target, step.appearTarget, step.advanceTo]);

  useEffect(() => {
    track("guide_step_viewed", {
      guide_id: guideId,
      step_index: stepIndex,
      target: step.target ?? null,
    });
  }, [guideId, stepIndex, step.target]);

  // Navigate-advancement: the real action for many steps is going somewhere
  // (picking a client creates + opens the order). Advance when the pathname
  // changes to `advanceTo` (or any change). Independent of target resolution,
  // so it fires even if the highlighted element already unmounted.
  const startPathRef = useRef(pathname);
  useEffect(() => {
    if (advanceOn !== "navigate" || justRetreated) return;
    if (pathname === startPathRef.current) return;
    if (!step.advanceTo || (pathname?.startsWith(step.advanceTo) ?? false)) {
      advanceGuide();
    }
  }, [advanceOn, pathname, step.advanceTo, justRetreated]);

  // Bound the navigate wait: a doomed `advanceTo` (a path the flow can never
  // reach, or a wrong AI-authored value) would otherwise linger forever —
  // invisibly, since a navigate step with no resolved target renders nothing.
  // One-shot on entry; the advance above unmounts the step (clearing this) the
  // instant navigation actually happens.
  useEffect(() => {
    if (advanceOn !== "navigate" || justRetreated) return;
    const timer = window.setTimeout(failStep, ACTION_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [advanceOn, justRetreated, failStep]);

  // Appear-advancement: for a step in a transient view (the New Order
  // calendar), the real action swaps a new element in. Advance when it mounts,
  // detected by the same MutationObserver wait as target resolution.
  useEffect(() => {
    // Suppressed right after "Previous": the appearTarget is likely still in
    // the DOM (the action was already done), which would instantly bounce the
    // user forward again. They advance with the now-enabled Next instead.
    if (advanceOn !== "appear" || !step.appearTarget || justRetreated) return;
    const controller = new AbortController();
    waitForGuideTarget(step.appearTarget, {
      signal: controller.signal,
      timeoutMs: ACTION_TIMEOUT_MS,
    }).then((el) => {
      if (controller.signal.aborted) return;
      // Mounted → advance. Timed out (never mounted) → fail fast rather than
      // sit on a live-looking spotlight whose advance is no longer listened for.
      if (el) advanceGuide();
      else failStep();
    });
    return () => controller.abort();
  }, [advanceOn, step.appearTarget, justRetreated, failStep]);

  // Resolve the target once we're on the right route. A never-resolving target
  // is handled by the terminal missing-effect below (toast on genuine drift).
  // For navigate steps a missing target is SOFT: the ring just doesn't draw;
  // the step still advances on navigation.
  useEffect(() => {
    if (!onRoute || !step.target) return;
    const controller = new AbortController();
    waitForGuideTarget(step.target, {
      signal: controller.signal,
      timeoutMs: step.optional ? OPTIONAL_TARGET_TIMEOUT_MS : undefined,
    }).then((found) => {
      if (controller.signal.aborted) return;
      if (found) {
        everResolvedRef.current = true;
        setEl(found);
        setStatus("found");
        found.scrollIntoView({
          block: "center",
          behavior: reducedMotion ? "auto" : "smooth",
        });
      } else {
        setStatus("missing");
      }
    });
    return () => controller.abort();
  }, [onRoute, step.target, step.optional, attempt, reducedMotion]);

  // Leaving the step's route mid-run (sidebar link, browser Back, an in-page
  // action): drop the resolved target so the modal shield — armed on `el` —
  // tears down instead of swallowing every click on the NEW page behind a
  // stale, unmatchable hole. Re-resolution starts clean if the user returns.
  useEffect(() => {
    if (!onRoute) {
      setEl(null);
      setStatus("resolving");
    }
  }, [onRoute]);

  // Terminal handling of a missing target (non-navigate steps only; navigate
  // steps keep waiting for the route change). Three cases, no mid-screen card:
  //  - OPTIONAL step never resolved → skip it: advance (completing the guide if
  //    it's the last step), so a conditional control that isn't on screen — e.g.
  //    the line-edit Save/Cancel bar on an order with no staged edits — ends in
  //    success, not the "couldn't continue" error.
  //  - required step never resolved → genuine drift/broken guide → toast + analytics.
  //  - resolved then lost (user closed the popover / walked away) → end silent.
  // The CI drift-guard is the real safety net for required targets.
  useEffect(() => {
    if (status !== "missing" || advanceOn === "navigate") return;
    if (!everResolvedRef.current) {
      if (step.optional) {
        advanceGuide();
        return;
      }
      toast.error(t("guide.failed"));
      track("guide_target_missing", {
        guide_id: guideId,
        step_index: stepIndex,
        target: step.target ?? "",
      });
    }
    dismissGuide();
  }, [status, advanceOn, guideId, stepIndex, step.target, step.optional, t]);

  // Virtualized rows / switching popover sub-views can detach the element
  // mid-step: re-resolve a bounded number of times, then give up (soft for
  // navigate steps, "out of date" for the rest).
  const onDetached = useCallback(() => {
    setEl(null);
    setAttempt((a) => {
      if (a + 1 >= MAX_REATTACH_ATTEMPTS) {
        setStatus("missing");
        return a;
      }
      setStatus("resolving");
      return a + 1;
    });
  }, []);

  const rect = useTrackedRect(el, onDetached);

  // Cutout element: the target's nearest floating container (popover / dialog)
  // so the whole popover shows through the dim, not just the ringed target.
  const spotlightEl = useMemo(
    () => (el ? spotlightElementFor(el) : null),
    [el],
  );
  const holeRect = useTrackedRect(spotlightEl);

  // A step's target is usually the TRIGGER (a date-cell button, a picker), and
  // the popover/dropdown/calendar it opens is portaled elsewhere at a z-index
  // BELOW the guide backdrop — so it gets dimmed unless it too gets a cutout.
  // Each open floating surface becomes its OWN hole (stitched, not unioned into
  // the target's), so whatever the user opened stays bright without the dim
  // between them turning into one giant bright rectangle.
  const openFloatingRects = useOpenFloatingRects(status === "found");

  // Computed corner radius, read once per element (cheap; changes rarely).
  const [targetRadius, setTargetRadius] = useState(8);
  const [holeRadius, setHoleRadius] = useState(8);
  useEffect(() => {
    if (el) setTargetRadius(readRadius(el));
  }, [el]);
  useEffect(() => {
    if (spotlightEl) setHoleRadius(readRadius(spotlightEl));
  }, [spotlightEl]);

  // click-target advancement: one capture-phase listener on the live element.
  // Capture fires for clicks on the element AND its descendants, so clicking a
  // row inside a highlighted list counts as the real action.
  const clickAdvances = advanceOn === "click-target";
  useEffect(() => {
    if (!clickAdvances || !el) return;
    const onClick = () => advanceGuide();
    el.addEventListener("click", onClick, { capture: true, once: true });
    return () => el.removeEventListener("click", onClick, { capture: true });
  }, [clickAdvances, el]);

  // Is a clicked node part of the current step's interaction (so it must NOT be
  // blocked)? The guide's own chrome, the spotlit element and its
  // floating container, the declared safe zone, OR any floating surface the
  // interaction opened. The last is why this is element-based, not geometric: a
  // target's editor opens in a PORTAL at the document root (a picker dropdown, a
  // calendar, a NESTED dialog) that `el.contains` misses — and evaluating it at
  // event time can't race a not-yet-measured cutout the way a precomputed hole
  // can (the New-Order calendar bug).
  const isInteractionAllowed = useCallback(
    (node: Element | null): boolean => {
      if (!node) return false;
      if (node.closest('[data-testid^="guide-"]')) return true;
      if (el?.contains(node)) return true;
      if (spotlightEl?.contains(node)) return true;
      if (step.safeZone) {
        const safe = queryGuideTarget(step.safeZone);
        if (safe?.contains(node)) return true;
      }
      return isAboveGuide(node);
    },
    [el, spotlightEl, step.safeZone],
  );

  // Modal blocking: swallow pointer/click events in the dim area so only the
  // spotlit element + whatever it opens stay live. A click outside never
  // dismisses — only the × / Esc end the guide. Element-based (see
  // `isInteractionAllowed`) rather than a geometric clip-path shield, so a
  // portaled calendar/dropdown the user opens is clickable immediately — no
  // dependence on its cutout being measured first.
  useEffect(() => {
    // Only shield while on-route with a live target. Gating on `onRoute` (not
    // just `el`) guarantees the capture-phase blockers are removed the moment
    // the user leaves the step's route — even before the off-route reset above
    // has cleared `el` — so clicks on the new page are never swallowed.
    if (!el || !onRoute) return;
    const block = (e: Event) => {
      if (isInteractionAllowed(e.target as Element | null)) return;
      e.preventDefault();
      e.stopImmediatePropagation();
    };
    const opts = { capture: true } as const;
    document.addEventListener("pointerdown", block, opts);
    document.addEventListener("mousedown", block, opts);
    document.addEventListener("click", block, opts);
    return () => {
      document.removeEventListener("pointerdown", block, opts);
      document.removeEventListener("mousedown", block, opts);
      document.removeEventListener("click", block, opts);
    };
  }, [el, onRoute, isInteractionAllowed]);

  const isLastStep = stepIndex === stepCount - 1;
  // An ACTION step advances on the user's real action (click / navigate /
  // appear); the advance button then only NUDGES ("try it yourself") and never
  // advances. An explain (`next`) step's button is the real advance. EXCEPTION:
  // right after "Previous" the action is likely already done (its auto-advance
  // is suppressed above), so enable Next there too — otherwise the user is stuck
  // on a satisfied action step with no forward affordance.
  const isActionStep = advanceOn !== "next";
  const canManualAdvance = !isActionStep || justRetreated;
  const forwardLabel = isLastStep ? t("guide.done") : t("guide.next");
  const advanceLabel = canManualAdvance ? forwardLabel : t("guide.tryYourself");

  // Anchored step: dim backdrop with a cutout around the target's floating
  // container (so a popover stays bright), a violet ring on the target, and a
  // caption. Padding + radius adapt to each element's size.
  const anchored = (r: TrackedRect) => {
    const base = holeRect ?? r;
    const baseRadius = holeRect ? holeRadius : targetRadius;
    // One stitched hole for the target's container, plus one per open floating
    // surface (a popover/dropdown/calendar sits at z below the dim). Each keeps
    // its own tight bounds so the dim between them stays dark.
    const holes = [
      paddedHole(base, baseRadius),
      ...openFloatingRects.map((fr) => paddedHole(fr, POPOVER_RADIUS)),
    ];
    // The caption anchors to the WHOLE bright region (target + anything it
    // opened), so it moves out of the way as a popover/calendar grows the
    // clickable area — not just when the element itself resizes. Cheap: a union
    // of rects already tracked. The ring still marks the element only.
    const captionAnchor = openFloatingRects.reduce<TrackedRect>(
      (acc, fr) => unionRect(acc, fr) ?? acc,
      base,
    );
    return (
      <>
        <GuideBackdrop holes={holes} reducedMotion={reducedMotion} />
        <GuideSpotlight
          rect={r}
          radius={targetRadius}
          padding={adaptivePadding(r)}
          reducedMotion={reducedMotion}
        />
        {/* The description rides in the caption anchored to the spotlit element
            (below it, or flipped above when there's no room), so it tracks the
            element's size/position. It is text-only; the × and Next live in the
            fixed control row below. */}
        <GuideCaption
          rect={captionAnchor}
          text={step.body}
          preferredSide={step.captionSide}
        />
        {/* One persistent control row (exit + Previous/Next) pinned to the same
            spot every step (per-step overridable). Button disabled + "try it
            yourself" on action steps; the live "Suivant"/"Terminé" on
            explain/last steps. */}
        <GuideAdvanceButton
          label={advanceLabel}
          onNext={advanceGuide}
          nextDisabled={!canManualAdvance}
          position={
            step.advanceButtonPos ?? chooseAdvanceButtonPos(captionAnchor)
          }
          onLeave={dismissGuide}
          onPrev={retreatGuide}
          prevDisabled={stepIndex === 0}
        />
      </>
    );
  };

  // Centered card — only for the cases that need a button (wrong-page nav,
  // out-of-date notice, pure info steps) or a target we couldn't anchor.
  // `overrideBody` replaces the step's own body when the card is ABOUT the guide
  // (e.g. the off-route prompt) rather than about the step's control.
  const centeredCard = (
    action?: { label: string; onClick: () => void },
    overrideBody?: string,
  ) => (
    <GuideCallout
      step={overrideBody ? { ...step, body: overrideBody } : step}
      stepIndex={stepIndex}
      stepCount={stepCount}
      anchor={null}
      advanceOn={advanceOn}
      isLastStep={isLastStep}
      onNext={advanceGuide}
      onDismiss={dismissGuide}
      action={action}
    />
  );

  if (!onRoute) {
    // Wrong page: centered card offering navigation (never yank the user). The
    // body is ABOUT the guide, not the step's control — at step 0 the guide was
    // launched off its start page; past it the user wandered off mid-guide.
    return centeredCard(
      {
        label: t("guide.takeMeThere"),
        onClick: () => navigate(step.route ?? "/"),
      },
      t(stepIndex === 0 ? "guide.offRouteStart" : "guide.offRouteResume"),
    );
  }

  // Navigate steps: soft target. Ring + caption once resolved; otherwise render
  // NOTHING and keep waiting for the route change (no mid-screen card — a
  // missing target here just means "not on screen yet"). Targetless navigate
  // steps show the centered card since that's their only chrome.
  if (advanceOn === "navigate") {
    if (!step.target) return centeredCard();
    if (status === "found" && rect) return anchored(rect);
    return null;
  }

  // A missing target on a non-navigate step is handled by the terminal
  // missing-effect (toast on drift, else silent) which ends the guide — render
  // nothing here, no mid-screen "out of date" card.
  if (!step.target) return centeredCard(); // pure info / next step

  if (status !== "found" || !rect) return null; // resolving / missing

  return anchored(rect);
}
